import { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useData } from '@microsoft/teamsfx-react';
import { Spinner } from '@fluentui/react-components';

import EnhancedExternalLink from 'src/components/atoms/enhanced-external-link/EnhancedExternalLink';
import WidgetContainer from 'src/components/molecules/widget-container/WidgetContainer';
import { useStore } from 'src/hooks/useStore';
import HealthGoalsInterstitial from 'src/components/organisms/health-goals-interstitial/HealthGoalsInterstitial';
import { MemberGoalCompletedResult } from 'src/models/health-goals/MemberGoalCompletedResult';
import { MemberGoalResult } from 'src/models/health-goals/MemberGoalResult';
import { MAXIMUM_PINNED_HEALTH_GOALS, HEALTH_GOALS_PAGE } from 'src/utils/constants';
import { getWebMdPage } from 'src/utils/utils';
import HealthGoalCard from '../health-goal-card/HealthGoalCard';
import { useHealthGoalsSectionStyles } from './HealthGoalsSection.styles';

const healthGoalsUrl = getWebMdPage(HEALTH_GOALS_PAGE);

const HealthGoalsSection: React.FC = () => {
  const styles = useHealthGoalsSectionStyles();
  const { healthGoalsStore } = useStore();
  const { loading, error } = useData(healthGoalsStore.getHealthGoals);

  if (error) {
    throw error;
  }

  if (loading) {
    return <Spinner aria-label="Loading health goals" />;
  }

  const pinnedHealthGoals = healthGoalsStore.pinnedHealthGoals;
  const getLimitPinnedCards = (): ReactNode => {
    return pinnedHealthGoals
      .slice(0, MAXIMUM_PINNED_HEALTH_GOALS)
      .map((pinnedHealthGoal: MemberGoalCompletedResult | MemberGoalResult) => (
        <HealthGoalCard pinnedHealthGoal={pinnedHealthGoal} key={pinnedHealthGoal.healthGoalId} />
      ));
  };

  const renderHealthGoals = (): React.ReactNode => {
    if (pinnedHealthGoals.length > 0) {
      return getLimitPinnedCards();
    }

    return <HealthGoalsInterstitial />;
  };

  const footer = <EnhancedExternalLink href={healthGoalsUrl}>View goals</EnhancedExternalLink>;

  return (
    <WidgetContainer title="Health Goals" footer={footer}>
      <div className={styles.cardContainer}>
        {renderHealthGoals()}
      </div>
    </WidgetContainer>
  );
};

export default observer(HealthGoalsSection);
